import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { DEBOUNCE_TIMERS, scrollTo, useDebounce } from '@/utils'
import { useLazyGlobalSearchQuery } from '@/redux/lookup'
import { trendingSearchData } from './BannerSearch.data'
import { BannerSearchProps } from './BannerSearch.types'

interface UseBannerSearchControllerOptions extends Pick<BannerSearchProps, 'noFullscreenSearch'> {
  isMobile: boolean
  hasExternalOnChange: boolean
}

export const useBannerSearchController = ({
  isMobile,
  hasExternalOnChange,
  noFullscreenSearch,
}: UseBannerSearchControllerOptions) => {
  const [searchList, setSearchList] = useState(trendingSearchData)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [keyword, setKeyword] = useState('')
  const [autoCompleteOpen, setAutoCompleteOpen] = useState(false)
  const [mobileModalOpen, setMobileModalOpen] = useState(false)
  const latestKeywordRef = useRef('')

  const [fetchSearch] = useLazyGlobalSearchQuery()

  const useFullscreenModal = isMobile && !noFullscreenSearch

  const getSuggestions = useDebounce(async (value: string) => {
    const response = await fetchSearch(value).unwrap()
    /**
     * Why this check ?
     * Because the user might have changed the input value before the api return.
     */

    if (latestKeywordRef.current === value) setSearchList(response.data.data)
  }, DEBOUNCE_TIMERS.GLOBAL_SEARCH)

  const onKeywordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      setKeyword(value)

      const trimmedValue = value.trim()
      latestKeywordRef.current = trimmedValue
      if (!trimmedValue) return setSearchList(trendingSearchData)

      getSuggestions(trimmedValue)
    },
    [getSuggestions],
  )

  const onWrapperClick = useCallback(() => {
    if (!useFullscreenModal) return
    setMobileModalOpen(true)
  }, [useFullscreenModal])

  const openAutoComplete = useCallback(() => {
    if (!useFullscreenModal && !hasExternalOnChange) setAutoCompleteOpen(true)
  }, [useFullscreenModal, hasExternalOnChange])

  const onInputFocus = useCallback(() => {
    openAutoComplete()
  }, [openAutoComplete])

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!isMobile) onKeywordChange(event)

      openAutoComplete()
    },
    [isMobile, onKeywordChange, openAutoComplete],
  )

  const onMobileModalClose = useCallback(() => {
    setMobileModalOpen(false)
    setKeyword('')
    setSearchList(trendingSearchData)
    latestKeywordRef.current = ''
  }, [])

  const onScrollToDropdown = useCallback(() => {
    if (autoCompleteOpen && wrapperRef.current) {
      const searchRectangle = wrapperRef.current.getBoundingClientRect()
      const dropdownHeight = 400
      const spaceBelow = window.innerHeight - searchRectangle.bottom

      if (spaceBelow < dropdownHeight) {
        const scrollAmount = dropdownHeight - spaceBelow + 50

        scrollTo(scrollAmount)
      }
    }
  }, [autoCompleteOpen])

  const lockBodyScroll = useCallback(() => {
    const scrollY = window.scrollY

    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    document.body.style.overflowY = 'scroll'

    return () => {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.overflowY = ''
      window.scrollTo(0, scrollY)
    }
  }, [])

  useEffect(() => {
    if (!mobileModalOpen) return
    return lockBodyScroll()
  }, [mobileModalOpen, lockBodyScroll])

  useEffect(() => {
    onScrollToDropdown()
  }, [onScrollToDropdown])

  return {
    keyword,
    searchList,
    autoCompleteOpen,
    setAutoCompleteOpen,
    mobileModalOpen,
    onWrapperClick,
    onInputFocus,
    onInputChange,
    onKeywordChange,
    onMobileModalClose,
    wrapperRef,
  }
}
